import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'
import type { CatalogHit, Evidence, LigandStart } from '../api/types'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Evidence entries as plain text, one string per entry, for a CSV cell.
 *
 * Every part that is present is kept, because which parts exist is itself the
 * distinction: a literature entry has an id and a resolvable url, while a
 * knowledge-graph entry has an id, NO url — there is no public resolver for a `KG:`
 * accession — and a `note` naming the field and value it supports. Dropping the
 * note to keep the cell short is what made a row with four KG citations read as a
 * row with no evidence, and an export is read further from its context than a
 * table is.
 */
export function formatEvidenceList(
  evidence: Evidence[] | null | undefined,
): string[] {
  if (!evidence) return []
  return evidence.map((e) =>
    [e.type, e.id, e.note, e.url].filter(Boolean).join(' '),
  )
}

/**
 * Ligand starting points → one string per catalog hit, for a CSV cell.
 *
 * One home, because both exports need it: the per-run table and the cumulative
 * board carry the same records under different keys (`ligand_starts` and
 * `latest_ligand_starts`), and two copies of this would drift the first time the
 * record shape changed — with nothing to say which export a given cell came from.
 *
 * `[homolog]` rides inside the PDB token rather than getting a column. A
 * spreadsheet loses columns, gets filtered and gets pasted into e-mail, and the
 * provenance may not be the thing that goes missing: a structure of a bacterial or
 * human relative does not establish this protein's pocket. A ligand with no catalog
 * hit is still listed, because "we found the ligand and nothing matched it" is a
 * different statement from "we found no ligand".
 */
export function formatLigandStarts(
  starts: LigandStart[] | null | undefined,
): string[] {
  if (!starts) return []
  return starts.flatMap((s) => {
    const where = `${s.pdb_id}${s.structure_provenance === 'homolog' ? '[homolog]' : ''}`
    const hits = s.catalog_hits ?? []
    if (hits.length === 0) return [`${where} ${s.comp_id} (no catalog hit)`]
    return hits.map(
      (h) =>
        `${where} ${s.comp_id} ${h.source ?? '?'}:${h.catalog_id ?? h.compound_id}@${h.similarity ?? '?'}`,
    )
  })
}

/**
 * Where a catalog hit's id resolves, or `null` when nothing resolves it.
 *
 * One home, because the per-run drawer and the board drawer both render the same
 * id and `search_compound_catalog` can name three catalogs
 * (`ligand_starts.CATALOG_DATASETS`) — `tests/test_ligand_starts.py` asserts that
 * vocabulary against this map, so a fourth catalog cannot arrive as a silently
 * unlinked id.
 *
 * `chemspace` deliberately returns `null`: its `catlog` id resolves nowhere, and a
 * guessed url that 404s is worse than plain text, because a dead link reads as
 * "this id is wrong" rather than "we have no store page for this catalog". A
 * chemspace hit is identified by its SMILES instead — see `smilesSearchUrls`.
 *
 * The id linked is the one **rendered** (`catalog_id ?? compound_id`) — linking a
 * different string than the reader clicked would send a chemist to a page for a
 * compound they were not looking at.
 */
export function compoundUrl(hit: CatalogHit): string | null {
  const id = hit.catalog_id ?? hit.compound_id
  if (!id) return null
  switch (hit.source?.toLowerCase()) {
    case 'corteva':
      return `https://simmate.prod.rd.corteva.com/data/CortevaCoRe/${encodeURIComponent(id)}/`
    case 'enamine':
      return `https://enaminestore.com/catalog/${encodeURIComponent(id)}?cat=real`
    default:
      return null
  }
}

/**
 * Store searches for a structure, for the catalog whose ids resolve nowhere
 * (`chemspace`).
 *
 * Enamine's results route, not `/search`: `/search?q=` stops at "choose a category",
 * and without `type` the store reads `q` as a catalog *code*. With `cat=REALDB` and
 * `sstype=EXACT` it runs the exact SMILES search on the REAL backend these hits come
 * from (`isChemSpaceSmilesExactSearch` in the store's bundle). `SMILEs` is their
 * spelling. No ChemSpace link: its search is a form post that lands on a compound
 * page keyed by its own `CSFS…` id, which unisearch does not return — any URL we
 * built opened an empty structure editor.
 */
export function smilesSearchUrls(smiles: string): { label: string; url: string }[] {
  const q = encodeURIComponent(smiles)
  return [
    {
      label: 'Enamine',
      url: `https://enaminestore.com/catalog?q=${q}&cat=REALDB&type=SMILEs&sstype=EXACT`,
    },
  ]
}

/** ISO timestamp → local short form. Empty string for null, never "Invalid Date". */
export function formatTimestamp(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Elapsed time between two ISO stamps, or from the first to now.
 *
 * A sweep runs for hours, so "3h 12m" is the useful unit; minutes alone stops
 * being readable well before a run finishes.
 */
export function formatDuration(
  startIso: string | null | undefined,
  endIso: string | null | undefined,
): string {
  if (!startIso) return '—'
  const start = new Date(startIso).getTime()
  const end = endIso ? new Date(endIso).getTime() : Date.now()
  if (Number.isNaN(start) || Number.isNaN(end) || end < start) return '—'
  const totalMinutes = Math.floor((end - start) / 60_000)
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`
}

/**
 * Numbers as they come off Postgres.
 *
 * `elo` is `double precision` and arrives as a JS number; `btl_score` is
 * `numeric`, which psycopg gives as Decimal and the API stringifies to preserve
 * precision (see api/routes/leaderboard.py::_serialisable). So both a number
 * and a numeric string are expected here, and neither is a bug.
 */
export function formatNumber(
  value: number | string | null | undefined,
  digits = 0,
): string {
  if (value === null || value === undefined || value === '') return '—'
  const n = typeof value === 'number' ? value : Number(value)
  if (Number.isNaN(n)) return '—'
  return n.toFixed(digits)
}
